import { type ChangeEvent, Fragment, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router';
import { Button, Input, ListBox, ListBoxItem, Popover, Select, SelectValue, Separator } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { debounce } from 'lodash';
import { parse } from 'query-string';

import { type AppState } from '../../reducer';
import { type Settings } from '../../settings';
import { Loading, LoadingError, Subhead } from '../../shared';
import { Box, Flex } from '../../shared/components';
import { Actions } from '../actions';
import { AccountList, SavedAccountList } from '../components';
import { SubscriptionStateSelect } from '../components/AccountSearch/SubscriptionStateSelect';
import { useAccountsList } from '../hooks/useAccountsList';
import { MetricType, NoAccountsMsg, type SavedAccount, type SubscriptionState } from '../model';
import { getFavoritedAccounts, getRecentlyViewedAccounts } from '../storageUtils';

import '../components/account-search-and-filters.scss';
import '../components/AccountList.scss';

type StateProps = Readonly<{
  settings: Settings;
}>;

type OwnProps = Readonly<{
  displayUnpaidOnly: boolean;
  overage?: string;
  displayWithoutQuery: boolean;
  showOverages: boolean;
}>;

type DispatchProps = Readonly<{
  onAddAccountToRecentlyViewed: (recentlyViewedAccount: SavedAccount) => void;
}>;

type IAccountListPageProps = StateProps & DispatchProps & OwnProps;

function AccountListPage({
  displayWithoutQuery,
  displayUnpaidOnly,
  overage,
  settings,
  onAddAccountToRecentlyViewed,
  showOverages,
}: IAccountListPageProps) {
  const routerLocation = useLocation();
  const routerNavigate = useNavigate();

  const queryParams = useMemo(() => parse(routerLocation.search), [routerLocation.search]);
  const initialQuery = (queryParams.q as string) || '';
  const fromZD = Boolean(queryParams.fromzd || queryParams.fromZD);
  const [query, setQuery] = useState<string>(initialQuery);
  const [prevInitialQuery, setPrevInitialQuery] = useState(initialQuery);
  if (initialQuery !== prevInitialQuery) {
    setPrevInitialQuery(initialQuery);
    setQuery(initialQuery);
  }
  const [filterName, setFilterName] = useState<string>('All');
  const [subStateFilter, setSubStateFilter] = useState<SubscriptionState | undefined>(undefined);
  const [selectedOverage, setSelectedOverage] = useState<string | undefined>(overage);

  const locationPathname = typeof routerLocation?.pathname === 'string' ? routerLocation.pathname : '';

  const showAccountList = displayWithoutQuery || query;
  const listParams = useMemo(
    () => ({ q: query, unpaid: displayUnpaidOnly, overage: selectedOverage as MetricType | 'any' | undefined }),
    [query, displayUnpaidOnly, selectedOverage],
  );

  const { isLoading, error, accounts } = useAccountsList(listParams, { enabled: Boolean(showAccountList) });
  const prevCountRef = useRef<number>(0);
  useEffect(() => {
    const prevCount = prevCountRef.current;
    if (fromZD && prevCount !== 1 && accounts && accounts.length === 1) {
      const basePath = (locationPathname || '').replace(/\/+$/, '');
      routerNavigate(`${basePath}/${accounts[0]._id}`);
    }
    prevCountRef.current = accounts ? accounts.length : 0;
  }, [accounts, fromZD, routerNavigate, locationPathname]);

  const renderOverageFilters = () => {
    const options = [
      { value: 'any', name: 'All' },
      { value: MetricType.SEATS, name: 'Seats' },
      { value: MetricType.MONTHLY_ACTIVE_USERS, name: 'Client MAUs' },
      { value: MetricType.EVENTS_RECEIVED, name: 'Experiments' },
      { value: MetricType.EVENTS_PUBLISHED, name: 'Streaming data export' },
    ];

    const handleSelectionChange = (key: string | number | null) => {
      if (key) {
        const selectedOption = options.find((option) => option.value === key);
        if (selectedOption) {
          setFilterName(selectedOption.name);
          setSelectedOverage(String(selectedOption.value));
        }
      }
    };

    return (
      <div className="overage-filters">
        <strong className="overage-filters__label">Overage type:</strong>
        <Select
          value={filterName === 'All' ? 'any' : options.find((opt) => opt.name === filterName)?.value || 'any'}
          onChange={handleSelectionChange}
          className="overage-filters__select"
          aria-label="Filter by overage type"
        >
          <Button>
            <Icon name="warning" slot="start-icon" />
            <SelectValue />
            <Icon name="chevron-down" slot="end-icon" />
          </Button>
          <Popover>
            <ListBox>
              {options.map((option, index) =>
                index === 1 ? (
                  <Fragment key={`group-${index}`}>
                    <ListBoxItem key={option.value} id={option.value} textValue={option.name}>
                      {option.name}
                    </ListBoxItem>
                    <Separator key={`separator-${index}`} />
                  </Fragment>
                ) : (
                  <ListBoxItem key={option.value} id={option.value} textValue={option.name}>
                    {option.name}
                  </ListBoxItem>
                ),
              )}
            </ListBox>
          </Popover>
        </Select>
      </div>
    );
  };

  const renderSubscriptionFilter = () => (
    <SubscriptionStateSelect subStateFilter={subStateFilter} setSubStateFilter={(item) => setSubStateFilter(item)} />
  );

  const search = useMemo(
    () =>
      debounce((q: string) => {
        const searchParams = q ? `q=${encodeURIComponent(q)}` : '';
        const basePath = (locationPathname || '').replace(/\/+$/, '');
        routerNavigate(`${basePath}${searchParams ? `?${searchParams}` : ''}`);
      }, 250),
    [routerNavigate, locationPathname],
  );

  useEffect(() => () => search.cancel(), [search]);

  const handleQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newQuery = event.target.value;
    setQuery(newQuery);
    search(newQuery);
  };

  const renderFilters = () => {
    if (!showAccountList) {
      return null;
    }
    return (
      <div className="AccountList-filters">
        {overage && renderOverageFilters()}
        {settings.enableSubscriptionStateFiltering && showAccountList && renderSubscriptionFilter()}
      </div>
    );
  };

  const addAccountToRecentlyViewed = (recentlyViewedAccount: SavedAccount) => {
    onAddAccountToRecentlyViewed(recentlyViewedAccount);
  };

  const renderEmptyState = () => (
    <>
      <p className="account-search-description">
        Search accounts by Organization (name or id) or by Member (email address or id).
      </p>
      <p>
        <Link to="unpaid">Unpaid accounts</Link> &middot; <Link to="all">All accounts</Link>{' '}
        {settings.enableOverages && (
          <>
            &middot; <Link to="overages">Accounts with overages</Link>
          </>
        )}
      </p>
      <>
        {settings.recentlyViewedAccounts && (
          <Flex>
            <Box className="Recently-Viewed-Table-Holder" width={1 / 2}>
              <SavedAccountList
                onAddAccountToRecentlyViewed={addAccountToRecentlyViewed}
                noAccountsMsg={NoAccountsMsg.RECENTLY_VIEWED}
                savedAccounts={getRecentlyViewedAccounts()}
                type="recent"
              />
            </Box>
            <Box className="Favorited-Table-Holder" width={1 / 2}>
              <SavedAccountList
                onAddAccountToRecentlyViewed={addAccountToRecentlyViewed}
                noAccountsMsg={NoAccountsMsg.FAVORITED}
                savedAccounts={getFavoritedAccounts()}
                type="favorited"
              />
            </Box>
          </Flex>
        )}
      </>
    </>
  );

  let accountListContent: ReactNode;
  if (isLoading) {
    accountListContent = <Loading title="Loading accounts…" />;
  } else if (error) {
    accountListContent = (
      <LoadingError
        title="Error loading accounts"
        description={error instanceof Error ? error.message : String(error)}
      />
    );
  } else if (showAccountList) {
    accountListContent = (
      <AccountList
        onAddAccountToRecentlyViewed={addAccountToRecentlyViewed}
        accounts={accounts.filter((a) => !subStateFilter || !a.subscription || a.subscription.state === subStateFilter)}
        showOverages={showOverages}
        filterName={filterName}
        noAccountsMsg={NoAccountsMsg.DEFAULT}
        settings={settings}
      />
    );
  } else {
    accountListContent = renderEmptyState();
  }

  return (
    <>
      {overage && <Subhead>Professional plans with overages</Subhead>}
      {displayUnpaidOnly && <Subhead>Unpaid accounts</Subhead>}
      {!displayWithoutQuery && (
        <Flex>
          <Box mb={2} className="account-search-input">
            <Input onChange={handleQueryChange} value={query} placeholder="Start typing to search" autoFocus />
          </Box>
        </Flex>
      )}
      {renderFilters()}
      {accountListContent}
    </>
  );
}

const mapStateToProps: (state: AppState) => StateProps = (state) => ({
  settings: state.settings,
});

const mapDispatchToProps = {
  onAddAccountToRecentlyViewed: Actions.addAccountToRecentlyViewed,
};

export const AccountListPageContainer = connect<StateProps, DispatchProps, OwnProps, AppState>(
  mapStateToProps,
  mapDispatchToProps,
)(AccountListPage);

export const OverageAccountListPageContainer = () => (
  <AccountListPageContainer displayWithoutQuery displayUnpaidOnly={false} showOverages overage="any" />
);

export const UnpaidAccountListPageContainer = () => (
  <AccountListPageContainer displayWithoutQuery displayUnpaidOnly showOverages={false} />
);

export const AllAccountListPageContainer = () => (
  <AccountListPageContainer displayWithoutQuery displayUnpaidOnly={false} showOverages={false} />
);
